import type { ReactNode } from "react";
import {
  ADMIN_PERMISSIONS,
  ASSIGNABLE_PERMISSIONS,
  ASSIGNABLE_ROLES,
  AdminPermissionKey,
  AdminPermissionState,
  buildAssignablePermissionState,
} from "../../../core/common/adminPermissions";
import ToggleSwitch from "../../../shared/ToggleSwitch";

export type AdminFormState = {
  name: string;
  email: string;
  password: string;
  role: "manager" | "support";
  isActive: boolean;
  permissions: AdminPermissionState[];
};

export const createEmptyAdminForm = (): AdminFormState => ({
  name: "",
  email: "",
  password: "",
  role: "manager",
  isActive: true,
  permissions: buildAssignablePermissionState(["dashboard"]),
});

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="form-field">
    <label className="modal-label">{label}</label>
    {children}
  </div>
);

export const AdminAccountFields = ({
  form,
  onChange,
  passwordOptional = false,
  disableActive = false,
}: {
  form: AdminFormState;
  onChange: (updates: Partial<AdminFormState>) => void;
  passwordOptional?: boolean;
  disableActive?: boolean;
}) => (
  <div className="admin-form-grid">
    <FormField label="Full Name">
      <input
        className="admin-input"
        value={form.name}
        onChange={(event) => onChange({ name: event.target.value })}
        required
      />
    </FormField>
    <FormField label="Email Address">
      <input
        type="email"
        className="admin-input"
        value={form.email}
        onChange={(event) => onChange({ email: event.target.value })}
        required
      />
    </FormField>
    <FormField
      label={passwordOptional ? "New Password (optional)" : "Password"}
    >
      <input
        type="password"
        className="admin-input"
        value={form.password}
        onChange={(event) => onChange({ password: event.target.value })}
        required={!passwordOptional}
        minLength={6}
        placeholder={
          passwordOptional
            ? "Leave blank to keep current password"
            : "Minimum 6 characters"
        }
      />
    </FormField>
    <FormField label="Role">
      <RoleSelect value={form.role} onChange={(role) => onChange({ role })} />
    </FormField>
    <div className="form-field admin-status-field">
      <ToggleSwitch
        variant="row"
        label="Status"
        hint="Admin can sign in to the panel when active"
        checked={form.isActive}
        disabled={disableActive}
        onChange={(isActive) => onChange({ isActive })}
      />
    </div>
  </div>
);

export const PermissionPicker = ({
  permissions,
  onToggle,
  onSetAll,
  disabled = false,
  hideHeader = false,
}: {
  permissions: AdminFormState["permissions"];
  onToggle: (key: AdminPermissionKey) => void;
  onSetAll?: (enabled: boolean) => void;
  disabled?: boolean;
  hideHeader?: boolean;
}) => {
  const enabledCount = permissions.filter((item) => item.isActive).length;

  return (
    <div className="permission-panel">
      {hideHeader ? (
        !disabled &&
        onSetAll && (
          <div className="permission-panel-actions">
            <p className="permission-panel-sub">
              {enabledCount} of {ASSIGNABLE_PERMISSIONS.length} modules enabled
            </p>
            <div className="page-hdr-actions">
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() => onSetAll(true)}
              >
                Enable All
              </button>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() => onSetAll(false)}
              >
                Clear All
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="permission-panel-head">
          <div>
            <h3 className="info-card-title">Panel Permissions</h3>
            <p className="permission-panel-sub">
              {enabledCount} of {ASSIGNABLE_PERMISSIONS.length} modules enabled
            </p>
          </div>
          {!disabled && onSetAll && (
            <div className="page-hdr-actions">
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() => onSetAll(true)}
              >
                Enable All
              </button>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() => onSetAll(false)}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}

      <div className="permission-grid">
        {ASSIGNABLE_PERMISSIONS.map((permission) => {
          const enabled = permissions.find(
            (item) => item.name === permission.key,
          )?.isActive;
          return (
            <div
              key={permission.key}
              className={`permission-item ${enabled ? "is-enabled" : ""} ${disabled ? "is-disabled" : ""}`}
            >
              <ToggleSwitch
                variant="compact"
                checked={Boolean(enabled)}
                disabled={disabled}
                onChange={() => onToggle(permission.key)}
                label={permission.label}
              />
              <div>
                <strong>{permission.label}</strong>
                <small>{permission.description}</small>
              </div>
              <span className={`status-badge ${enabled ? "success" : "muted"}`}>
                {enabled ? "Allowed" : "Denied"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const PermissionSummary = ({
  permissions,
}: {
  permissions: AdminPermissionState[];
}) => (
  <div className="permission-summary-grid">
    {ADMIN_PERMISSIONS.map((permission) => {
      const enabled = permissions.find(
        (item) => item.name === permission.key,
      )?.isActive;
      return (
        <div
          key={permission.key}
          className={`permission-summary-item ${enabled ? "is-enabled" : ""}`}
        >
          <strong>{permission.label}</strong>
          <span className={`status-badge ${enabled ? "success" : "muted"}`}>
            {enabled ? "Allowed" : "Denied"}
          </span>
        </div>
      );
    })}
  </div>
);

const RoleSelect = ({
  value,
  onChange,
  disabled = false,
}: {
  value: AdminFormState["role"];
  onChange: (role: AdminFormState["role"]) => void;
  disabled?: boolean;
}) => (
  <select
    className="admin-input"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value as AdminFormState["role"])}
  >
    {ASSIGNABLE_ROLES.map((role) => (
      <option key={role} value={role}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </option>
    ))}
  </select>
);
